'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { Popover, PopoverAnchor, PopoverContent } from '../../overlays/popover/index.js';
import { EventChip } from '../event-chip/index.js';
import { addDays, defaultZone, eventsByDay, formatDay, formatTime, monthDays, zonedParts, } from './calendar-month-view.dates.js';
import { localeWeekStart } from '../../../internal/dates/index.js';
/**
 * A month of days with their events: weekday header, today and outside-month
 * days marked, a "+N more" popover for busy days, arrow-key movement between
 * days, and callbacks for opening a day or an event. Below about 36rem of
 * container width, cells show a dot summary that opens the day's list.
 */
export const CalendarMonthView = forwardRef(function CalendarMonthView({ month, events = [], today: todayProp, weekStartsOn: weekStartsOnProp, fixedWeeks = true, maxEvents = 3, timeZone, locale, hourCycle, onDayClick, onEventClick, renderEvent, dayState, focusedDay: focusedProp, defaultFocusedDay, onFocusedDayChange, onNavigate, labels, className, 'aria-label': ariaLabel, ...rest }, ref) {
    const zone = timeZone ?? defaultZone();
    const hc = hourCycle === '12' ? 'h12' : hourCycle === '24' ? 'h23' : undefined;
    const weekStartsOn = weekStartsOnProp ?? localeWeekStart(locale);
    const days = useMemo(() => monthDays(month, weekStartsOn, fixedWeeks), [month, weekStartsOn, fixedWeeks]);
    const byDay = useMemo(() => eventsByDay(events, days, zone), [events, days, zone]);
    const monthKey = month.slice(0, 7);
    const [clockToday, setClockToday] = useState(null);
    useEffect(() => {
        if (todayProp !== undefined)
            return;
        setClockToday(zonedParts(new Date(), zone).day);
    }, [todayProp, zone]);
    const today = todayProp === undefined ? clockToday : todayProp;
    const fallbackFocus = today && today.slice(0, 7) === monthKey ? today : `${monthKey}-01`;
    const [innerFocus, setInnerFocus] = useState(defaultFocusedDay);
    const requested = focusedProp ?? innerFocus;
    const focused = requested && days.includes(requested) ? requested : fallbackFocus;
    const gridRef = useRef(null);
    const wantFocus = useRef(null);
    const setFocus = useCallback((day) => {
        if (focusedProp === undefined)
            setInnerFocus(day);
        if (day !== focused)
            onFocusedDayChange?.(day);
    }, [focusedProp, focused, onFocusedDayChange]);
    // Move DOM focus once the requested day is rendered (also after a month change).
    useEffect(() => {
        const target = wantFocus.current;
        if (!target)
            return;
        const btn = gridRef.current?.querySelector(`[data-date-button="${target}"]`);
        if (btn) {
            wantFocus.current = null;
            btn.focus();
        }
    });
    const moveTo = (day) => {
        setFocus(day);
        if (day.slice(0, 7) !== monthKey && onNavigate) {
            // Focus follows once the product has shown the new month.
            wantFocus.current = day;
            onNavigate(day);
            if (!days.includes(day))
                return;
        }
        gridRef.current?.querySelector(`[data-date-button="${day}"]`)?.focus();
    };
    const onDateKeyDown = (e, day) => {
        const rtl = e.currentTarget.closest('[dir="rtl"]') !== null;
        const col = days.indexOf(day) % 7;
        const shiftMonth = (n) => {
            const [y, m, d] = day.split('-').map(Number);
            const target = new Date(Date.UTC(y, m - 1 + n, 1));
            const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0));
            const dd = Math.min(d, last.getUTCDate());
            return `${target.getUTCFullYear()}-${String(target.getUTCMonth() + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
        };
        let next = null;
        switch (e.key) {
            case 'ArrowRight':
                next = addDays(day, rtl ? -1 : 1);
                break;
            case 'ArrowLeft':
                next = addDays(day, rtl ? 1 : -1);
                break;
            case 'ArrowDown':
                next = addDays(day, 7);
                break;
            case 'ArrowUp':
                next = addDays(day, -7);
                break;
            case 'Home':
                next = addDays(day, -col);
                break;
            case 'End':
                next = addDays(day, 6 - col);
                break;
            case 'PageUp':
                next = shiftMonth(e.shiftKey ? -12 : -1);
                break;
            case 'PageDown':
                next = shiftMonth(e.shiftKey ? 12 : 1);
                break;
            default:
                return;
        }
        e.preventDefault();
        // Without onNavigate, focus stays inside the shown weeks.
        if (!days.includes(next) && !onNavigate)
            return;
        moveTo(next);
    };
    const [openDay, setOpenDay] = useState(null);
    const opener = useRef(null);
    const L = {
        more: labels?.more ?? ((n) => `+${n} more`),
        day: labels?.day ?? ((date, n) => `${date}, ${n} event${n === 1 ? '' : 's'}`),
        today: labels?.today ?? 'today',
        summary: labels?.summary ??
            ((date, n) => `${n} event${n === 1 ? '' : 's'}, ${date}`),
    };
    const longDate = (day) => formatDay(day, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, locale);
    const chip = (ev, tabbable) => {
        const node = (_jsx(EventChip, { variant: "dot", title: ev.title, ...(ev.allDay ? {} : { time: formatTime(ev.start, zone, locale, hc) }), ...(ev.status ? { status: ev.status } : {}), ...(ev.color ? { color: ev.color } : {}), ...(ev.href !== undefined ? { href: ev.href } : {}), tabIndex: tabbable ? undefined : -1, ...(onEventClick ? { onClick: () => onEventClick(ev) } : {}) }));
        return renderEvent ? renderEvent(ev, node) : node;
    };
    const weeks = [];
    for (let i = 0; i < days.length; i += 7)
        weeks.push(days.slice(i, i + 7));
    const header = days.slice(0, 7);
    return (_jsx("div", { ref: ref, ...rest, className: cx('mn-calendar-month-view', className), children: _jsxs("div", { ref: gridRef, role: "grid", "aria-label": ariaLabel ?? formatDay(`${monthKey}-01`, { month: 'long', year: 'numeric' }, locale), className: "mn-calendar-month-view__grid", children: [_jsx("div", { role: "row", className: "mn-calendar-month-view__weekdays", children: header.map((d) => (_jsxs("div", { role: "columnheader", className: "mn-calendar-month-view__weekday", children: [_jsx("span", { "aria-hidden": true, children: formatDay(d, { weekday: 'short' }, locale) }), _jsx("span", { className: "mn-visually-hidden", children: formatDay(d, { weekday: 'long' }, locale) })] }, d))) }), weeks.map((week) => (_jsx("div", { role: "row", className: "mn-calendar-month-view__week", children: week.map((day) => {
                        const list = byDay.get(day) ?? [];
                        const count = list.length;
                        const state = dayState?.(day);
                        const isToday = day === today;
                        const outside = day.slice(0, 7) !== monthKey;
                        const isFocused = day === focused;
                        const shownCount = count > maxEvents ? Math.max(0, maxEvents - 1) : count;
                        const hidden = count - shownCount;
                        const name = [
                            L.day(longDate(day), count),
                            isToday ? L.today : null,
                            state?.badge ?? null,
                        ]
                            .filter(Boolean)
                            .join(', ');
                        const open = openDay === day;
                        const openList = (el) => {
                            opener.current = el;
                            setOpenDay(day);
                        };
                        const body = (_jsxs("div", { className: "mn-calendar-month-view__events-area", children: [shownCount > 0 ? (_jsx("ul", { className: "mn-calendar-month-view__events", children: list.slice(0, shownCount).map((ev) => (_jsx("li", { children: chip(ev, isFocused) }, ev.id))) })) : null, hidden > 0 ? (_jsx("button", { type: "button", className: "mn-calendar-month-view__more", tabIndex: isFocused ? undefined : -1, "aria-haspopup": "dialog", "aria-expanded": open, onClick: (e) => openList(e.currentTarget), children: L.more(hidden) })) : null, count > 0 ? (_jsxs("button", { type: "button", className: "mn-calendar-month-view__summary", tabIndex: isFocused ? undefined : -1, "aria-haspopup": "dialog", "aria-expanded": open, "aria-label": L.summary(longDate(day), count), onClick: (e) => openList(e.currentTarget), children: [list.slice(0, 3).map((ev) => (_jsx("span", { className: "mn-calendar-month-view__dot", "data-status": ev.status ?? 'confirmed', style: ev.color
                                                ? { ['--mn-event-color']: ev.color }
                                                : undefined }, ev.id))), count > 3 ? (_jsx("span", { className: "mn-calendar-month-view__dot-more", "data-more": count - 3, "aria-hidden": true })) : null] })) : null] }));
                        return (_jsxs("div", { role: "gridcell", className: "mn-calendar-month-view__day", "data-day": day, "data-today": isToday || undefined, "data-outside": outside || undefined, "data-hatched": state?.hatched || undefined, "data-focused": isFocused || undefined, onFocus: () => {
                                if (!isFocused)
                                    setFocus(day);
                            }, children: [_jsxs("div", { className: "mn-calendar-month-view__day-head", children: [_jsx("button", { type: "button", className: "mn-calendar-month-view__date", "data-date-button": day, tabIndex: isFocused ? 0 : -1, "aria-label": name, "aria-current": isToday ? 'date' : undefined, onKeyDown: (e) => onDateKeyDown(e, day), onClick: () => onDayClick?.(day), children: formatDay(day, { day: 'numeric' }, locale) }), state?.badge ? (_jsx("span", { className: "mn-calendar-month-view__badge", "aria-hidden": true, children: state.badge })) : null] }), count > 0 ? (_jsxs(Popover, { open: open, onOpenChange: (o) => setOpenDay(o ? day : null), children: [_jsx(PopoverAnchor, { asChild: true, children: body }), _jsxs(PopoverContent, { className: "mn-calendar-month-view__popover", "aria-label": longDate(day), onCloseAutoFocus: (e) => {
                                                e.preventDefault();
                                                opener.current?.focus();
                                            }, children: [_jsx("p", { className: "mn-calendar-month-view__popover-title", "aria-hidden": true, children: formatDay(day, { weekday: 'long', day: 'numeric', month: 'long' }, locale) }), _jsx("ul", { className: "mn-calendar-month-view__popover-list", children: list.map((ev) => (_jsx("li", { children: chip(ev, true) }, ev.id))) })] })] })) : (body)] }, day));
                    }) }, week[0])))] }) }));
});
//# sourceMappingURL=calendar-month-view.js.map